import { ApiError } from '@formosa/shared/api/createClient';
import travelClient from './travelClient';
import { travelEndpoints } from './endpoints';

/**
 * Preserve Travel snowflake IDs that exceed Number.MAX_SAFE_INTEGER on copy response.
 */
function parseCopyJson(raw) {
  if (typeof raw !== 'string' || !raw) return raw;
  const text = raw.replace(
    /("(?:journeyId|publicationId)"\s*:\s*)(\d{15,})/g,
    '$1"$2"',
  );
  return JSON.parse(text);
}

const copySnowflakeJson = { transformResponse: [parseCopyJson] };

/**
 * IM 分享／卡片解析回應中的 snowflake ID 一律保留為字串。
 */
function parseImShareJson(raw) {
  if (typeof raw !== 'string' || !raw) return raw;
  const text = raw.replace(
    /("(?:conversationId|journeyPublicationId|serverMessageId)"\s*:\s*)(\d{15,})/g,
    '$1"$2"',
  );
  return JSON.parse(text);
}

const imShareSnowflakeJson = { transformResponse: [parseImShareJson] };

/**
 * 公開詳情（含故事 CARD_V1）：snowflake ID 在 JSON.parse 前即轉為字串，
 * 不先轉 Number 再補救（19 位 cardId／mediaId 超過 Number.MAX_SAFE_INTEGER）。
 */
export function parsePublicJourneyDetailJson(raw) {
  if (typeof raw !== 'string' || !raw) return raw;
  const text = raw.replace(
    /("(?:publicationId|journeyId|creatorMemberId|dayId|activityId|locationId|cardId|memoryEntryId|mediaId)"\s*:\s*)(\d{15,})/g,
    '$1"$2"',
  );
  return JSON.parse(text);
}

const detailSnowflakeJson = { transformResponse: [parsePublicJourneyDetailJson] };

async function readBlobText(blob) {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  // jsdom／舊環境可能無 Blob.text
  return new Response(blob).text();
}

/** JSON 錯誤被包成 Blob 時還原為 ApiError；成功則確認為圖片 Blob。 */
async function resolveStoryMediaBlob(data, httpStatus = null) {
  if (!(data instanceof Blob)) {
    throw new ApiError('故事圖片回應無效', null, data, { httpStatus });
  }
  const type = String(data.type || '').toLowerCase();
  if (type.includes('application/json') || type.includes('text/json') || type.includes('text/plain')) {
    const text = await readBlobText(data);
    let payload;
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError('故事圖片載入失敗', null, text, { httpStatus });
    }
    const error = new ApiError(payload.message || '故事圖片載入失敗', payload.code, payload, { httpStatus });
    // Blob 錯誤不會經過共用 client 的 JSON 判斷：解析後交回同一 client，以相同規則判斷 token 過期並派發
    // formosa:auth-expired（同一 session 只通知一次）。只有真正的過期才標記，其餘 401／404／503 照舊。
    error.sessionExpired = travelClient.reportParsedErrorPayload?.(payload) === true;
    throw error;
  }
  if (type && !type.startsWith('image/') && type !== 'application/octet-stream') {
    throw new ApiError('故事圖片類型無效', null, { type }, { httpStatus: httpStatus ?? 502 });
  }
  return data;
}

async function rethrowStoryMediaError(cause) {
  if (cause instanceof ApiError && cause.raw instanceof Blob) {
    await resolveStoryMediaBlob(cause.raw, cause.httpStatus);
  }
  const blob = cause?.response?.data;
  if (blob instanceof Blob) {
    await resolveStoryMediaBlob(blob, cause.response?.status ?? cause.httpStatus ?? null);
  }
  throw cause;
}

/** 分享請求僅允許 conversationId／clientMessageId／message；其餘欄位一律由後端決定。 */
function shareToImBody({ conversationId, clientMessageId, message } = {}) {
  const body = {
    conversationId: String(conversationId),
    clientMessageId: String(clientMessageId),
  };
  const note = typeof message === 'string' ? message.trim() : '';
  if (note) body.message = note;
  return body;
}

export const publicJourneyApi = {
  getDetail: (publicationId) => travelClient.get(travelEndpoints.publicJourney(publicationId), detailSnowflakeJson),
  getTimeline: (publicationId, params) => travelClient.get(travelEndpoints.publicJourneyTimeline(publicationId), { params }),
  getEngagement: (publicationId) => travelClient.get(travelEndpoints.publicJourneyEngagement(publicationId)),
  /**
   * 授權取圖（READ-01）：binary Blob，走既有 Authorization；不用公共代理／objectKey。
   * JSON 錯誤回應不得當成圖片。
   */
  getStoryMedia: async (publicationId, mediaId, { signal } = {}) => {
    const pub = String(publicationId ?? '');
    const media = String(mediaId ?? '');
    if (!/^[1-9]\d*$/.test(pub) || !/^[1-9]\d*$/.test(media)) {
      throw new ApiError('故事圖片參數無效', null, null, { httpStatus: 404 });
    }
    try {
      const data = await travelClient.get(
        travelEndpoints.publicJourneyStoryMedia(pub, media),
        {
          responseType: 'blob',
          signal,
          transformResponse: [(value) => value],
        },
      );
      return resolveStoryMediaBlob(data);
    } catch (cause) {
      return rethrowStoryMediaError(cause);
    }
  },
  /** 將公開行程複製為會員自己的私人行程；publicationId 保持 opaque string。 */
  copy: (publicationId, { clientRequestId }) => travelClient.post(
    travelEndpoints.copyPublicJourney(publicationId),
    { clientRequestId },
    copySnowflakeJson,
  ),
  like: (publicationId) => travelClient.post(travelEndpoints.likePublicJourney(publicationId)),
  unlike: (publicationId) => travelClient.post(travelEndpoints.unlikePublicJourney(publicationId)),
  save: (publicationId) => travelClient.post(travelEndpoints.savePublicJourney(publicationId)),
  unsave: (publicationId) => travelClient.post(travelEndpoints.unsavePublicJourney(publicationId)),
  listSaved: (params) => travelClient.get(travelEndpoints.mySavedJourneys, { params }),
  /** 將公開行程分享到單一既有 IM 對話；重試須沿用同一 clientMessageId。 */
  shareToIm: (publicationId, payload) => travelClient.post(
    travelEndpoints.sharePublicJourneyToIm(publicationId),
    shareToImBody(payload),
    imShareSnowflakeJson,
  ),
  /** 讀取時批次重新授權行程卡片（最多 50 筆）；歷史快照不可作為授權依據。 */
  resolvePublications: (journeyPublicationIds) => travelClient.post(
    travelEndpoints.resolvePublicJourneyPublications,
    { journeyPublicationIds: (journeyPublicationIds || []).map(String) },
    imShareSnowflakeJson,
  ),
};
